import { useEffect, type ReactNode } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

export const SPIN_MS = 1100;
const SETTLE_MS = 170;
/** How far past the landing face the reel overshoots before snapping back. */
const OVERSHOOT = 7;

/** Total time from the reel's own delay to it sitting still. */
export const spinTotal = (delay: number) => delay + SPIN_MS + SETTLE_MS;

type Props = {
  /** The face the reel lands on. */
  landed: ReactNode;
  /** Blur faces the reel rushes past. Order is the spin order, last one first. */
  decoys: ReactNode[];
  /** Every face is clipped to this. Reels can't measure, so the caller owns it. */
  height: number;
  delay?: number;
  duration?: number;
  style?: StyleProp<ViewStyle>;
};

/**
 * A one-column slot machine reel: spins downward through `decoys` and settles
 * on `landed`.
 *
 * Faces are laid out `[buffer, landed, ...decoys]` — the buffer is what the
 * overshoot bounce peeks at, so the reel never flashes empty background as it
 * settles. Mount the reel under a key that changes per draw; the spin fires
 * once on mount.
 */
export function SlotReel({ landed, decoys, height, delay = 0, duration = SPIN_MS, style }: Props) {
  const reducedMotion = useReducedMotion();

  const faces: ReactNode[] = decoys.length > 0 ? [decoys[0], landed, ...decoys] : [landed, landed];
  const start = -(faces.length - 1) * height;
  const end = -height;

  const y = useSharedValue(reducedMotion ? end : start);

  useEffect(() => {
    if (reducedMotion) {
      y.value = end;
      return;
    }
    y.value = start;
    y.value = withDelay(
      delay,
      withSequence(
        withTiming(end + OVERSHOOT, { duration, easing: Easing.out(Easing.cubic) }),
        withTiming(end, { duration: SETTLE_MS, easing: Easing.inOut(Easing.quad) }),
      ),
    );
  }, [delay, duration, end, reducedMotion, start, y]);

  const reelStyle = useAnimatedStyle(() => ({ transform: [{ translateY: y.value }] }));

  /** Where `landed` sits in `faces` — everything else is filler. */
  const landedIndex = decoys.length > 0 ? 1 : 0;

  return (
    <View style={[styles.window, { height }, style]}>
      <Animated.View style={reelStyle}>
        {faces.map((face, index) => {
          // Filler is decoration; screen readers should only see the real face.
          const hidden = index !== landedIndex;
          return (
            <View
              key={index}
              style={[styles.face, { height }]}
              aria-hidden={hidden}
              accessibilityElementsHidden={hidden}
              importantForAccessibility={hidden ? 'no-hide-descendants' : 'auto'}>
              {face}
            </View>
          );
        })}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  window: {
    overflow: 'hidden',
    justifyContent: 'flex-start',
  },
  face: {
    justifyContent: 'center',
    overflow: 'hidden',
  },
});
